import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { Metamodel } from 'app/model/base/model';
import { HdfsCopySource } from 'app/model/pipeline/copysource/hdfs.copysource';
import { Constants } from 'app/shared/shared-constants';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import {
    commonFileTypeStoreWriteSettingControls,
    commonBinaryStoreWriteSettingControls,
    hdfsDistCpMetadata,
    getCommonBinaryStoreReadSettingControls,
    getCommonFileTypeStoreReadSettingControls,
    commonFileTypeStoreDeleteActivityReadSettings,
    cdcDestination
} from '../consts/metadata-consts';
import { binaryMetadata } from '../dataset/binary-metadata';
import { GlobalVariables } from 'app/services/globals';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { HdfsLinkedService, shellStringResources } from 'shell-models-lib';

export const hdfsMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: Metamodel.modelFor(HdfsLinkedService).name,
        displayName: shellStringResources.Hdfs_DisplayText,
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-HDFS'
        },
        isFirstClass: true,
        supportIncrementalBinaryCopy: true,
        notSupportCopySink: true,
        supportCopyJobSource: true,
        ...(GlobalVariables.copyJobGenericCdcPhase2 ? {
            cdc: {
                supportCDC: true,
                supportedCDCDestinations: cdcDestination,
                supportGenericCdc: true
            }
        } : {}),
        supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
        supportedInTrident: true,
        supportOPDG: GlobalVariables.enableOPDG,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'HdfsForPipeline' as ResourceKind
        },
        copySinkTypes: [],
        copySourceTypes: [Metamodel.modelFor(HdfsCopySource).name],
        locationPattern: {
            folderPath: '([^]*)/[^/]*',
            fileName: '(?:[^]*/)?([^/]*)'
        },
        mergeReadSettingsInTrident: true,
        locationPropertiesRequireParameters: ['folderPath', 'fileName'],
        pathRegex: {
            pattern: Constants.fileSharePathValidation,
            message: stringResources.CopyWizard_Dataset_File_Sink_FileSharePathValidationError
        },
        supportDatasetV2: true,
        category: ['File'],
        storeReadSettingControls: getCommonFileTypeStoreReadSettingControls().concat([hdfsDistCpMetadata]),
        storeWriteSettingControls: commonFileTypeStoreWriteSettingControls,
        datasetTypeToStoreReadSettingControls: {
            [binaryMetadata.type]: getCommonBinaryStoreReadSettingControls(false, false, false, false, true).concat([
                hdfsDistCpMetadata
            ])
        },
        datasetTypeToStoreWriteSettingControls: {
            [binaryMetadata.type]: commonBinaryStoreWriteSettingControls
        },
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        controls: [
            {
                type: 'textbox',
                bind: 'url',
                label: shellStringResources.ModelName_HdfsClusterUri,
                required: true,
                validators: [{ type: 'required', errorMessage: '' }],
                warningMessageCalculator: (value: string) =>
                    stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
                isLabelVertical: true
            },
            {
                type: 'select-switch',
                bind: 'authenticationType',
                label: stringResources.ModelName_AuthenticationType,
                options: [
                    {
                        label: stringResources.Anonymous_DisplayText,
                        value: 'Anonymous'
                    },
                    {
                        label: stringResources.AuthenticationType_Select_Windows_Option,
                        value: 'Windows'
                    }
                ],
                description: stringResources.Hdfs_AuthenticationTypeDescription,
                required: true,
                validators: [{ type: 'required', errorMessage: '' }],
                isLabelVertical: true,
                defaultValue: 'Anonymous',
                cases: [
                    {
                        caseValue: 'Windows',
                        children: [
                            {
                                type: 'textbox',
                                bind: 'userName',
                                label: stringResources.ModelName_UserName,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }],
                                isLabelVertical: true
                            },
                            {
                                type: 'dynamic-akv-switcher',
                                bind: 'password',
                                label: stringResources.ModelName_Password,
                                child: {
                                    type: 'password',
                                    label: stringResources.ModelName_Password,
                                    required: true,
                                    isLabelVertical: true
                                },
                                validators: [{ type: 'required', errorMessage: '' }],
                                description: format(
                                    stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                    format(
                                        stringResources.ModelName_LinkedServiceTemplate,
                                        shellStringResources.Hdfs_DisplayText
                                    ),
                                    stringResources.ModelName_Password
                                )
                            }
                        ]
                    },
                    {
                        caseValue: 'Anonymous',
                        children: [
                            {
                                type: 'textbox',
                                bind: 'userName',
                                label: stringResources.ModelName_UserName,
                                isLabelVertical: true
                            }
                        ]
                    }
                ]
            }
        ]
    };

    return metadata;
};
